import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Router} from "@angular/router";
import { Observable, Observer  } from 'rxjs';
import {SolrOptionsEntityDTO} from "@signature-it/ngx-entity/models";
import { RestUrlsConfig, GenericConfig } from "@signature-it/ngx-generic/config";
import { LanguageService } from "@signature-it/ngx-generic/services";
import { ILanguage } from "@signature-it/ngx-generic/interfaces";
import { SolrRequestDTO } from "@signature-it/ngx-generic/models";

declare let _: any;

@Injectable({
    providedIn: 'root'
})

export class SolrEntityService {

    protected getSolrUrl = `${this.config.appURL}/public/solr/entity`;

    constructor(
        protected http: HttpClient,
        protected config: RestUrlsConfig,
        protected router: Router,
        protected langSvc: LanguageService
    ) {}

    getSolr(options: SolrOptionsEntityDTO): Observable<any> {
        return new Observable((observer: Observer<any>) => {
            this.buildSolrUrl(
                options
            ).subscribe(responses => observer.next(responses));
        });
    }


    buildSolrUrl(options: SolrOptionsEntityDTO, langInField = true, availableFilters?) {
        const request = new SolrRequestDTO();
        const currLang = options.lang ?? this.langSvc.getCurrentLanguage();
        request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `lang_code_s:${currLang.code}`);
        request.addQueryParameter(SolrRequestDTO.PARAMETER_FACET, true);
        // request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `type_i:9`);
        request.addQueryParameter(SolrRequestDTO.PARAMETER_DEF_TYPE, 'edismax')
        if (options.itemsPerPage) {
            request.addQueryParameter(SolrRequestDTO.PARAMETER_ROWS, options.itemsPerPage);
        }
        if (options.pageNumber) {
            request.addQueryParameter(SolrRequestDTO.PARAMETER_START, ((options.pageNumber - 1) * options.itemsPerPage).toString());
        }
        if ( options.searchEntity) {
            request.addQueryParameter(SolrRequestDTO.PARAMETER_Q, '(' +options.searchEntity['full']['key'] + ' '+ SolrRequestDTO.PARAMETER_F_SEARCH + options.searchEntity['fields']['key'] +')');
            let searchInFull = options.searchEntity['full']['qf'].map(s => `${s}${langInField ? currLang.code : ''}_ngram`).join(' ');
            let searchInFields = options.searchEntity['fields']['qf'].map(s => `${s}${langInField ? currLang.code : ''}_ngram`).join(' ');
            request.addQueryParameter(SolrRequestDTO.PARAMETER_QF, [searchInFull, searchInFields].join(' '));
            request.addQueryParameter(SolrRequestDTO.PARAMETER_F_QF, searchInFields);
        }
        if (options.search) {
            request.addQueryParameter(SolrRequestDTO.PARAMETER_Q, options.search);
        }

        if (options.sort) {
            request.addQueryParameter(SolrRequestDTO.PARAMETER_SORT, options.sort);
        }

        if(options.isGroupBy) {
            request.addQueryParameter(SolrRequestDTO.PARAMETER_GROUP, true);
            request.addQueryParameter(SolrRequestDTO.PARAMETER_GROUP_NGROUPS, true);
            request.addQueryParameter(SolrRequestDTO.PARAMETER_GROUP_FIELD, options.groupField);
            if(options.groupLimit) {
                request.addQueryParameter(SolrRequestDTO.PARAMETER_GROUP_LIMIT, options.groupLimit);
            }
        }

        if(options.isFacet) {
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FACET, true);
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FACET_LIMIT, 2000);
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FACET_FIELD, 'node_id_is');
        }

        if(options.entityTypeVar) {
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `entity_type_var_s:${options.entityTypeVar}`);
        }
        if(options.entityVar) {
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `entity_var_s:${options.entityVar}`);
        }
        if(options.entityUri) {
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `uri_s:${options.entityUri}`);
        }
        if(options.parentNodeId) {
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `node_id_is:${options.parentNodeId}`);
        }
        if(Array.isArray(options.additionalFQ)) {
            options.additionalFQ.forEach(item => {
                request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, item);
            })
        }
        if (options.searchFields) {
            const searchInFields = options.searchFields.map(s => `${s}${langInField ? currLang.code : ''}_ngram`).join(' ');
            request.addQueryParameter(SolrRequestDTO.PARAMETER_QF, searchInFields);
        }
        if (options.queryFields) {
            request.addQueryParameter(SolrRequestDTO.PARAMETER_QF, options.queryFields);
        }

        if (availableFilters && availableFilters.length > 0) {
            this.handleAvailableFilters(availableFilters, request);
        }
        if(options.productType?.length) {
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `product_type_s:(${options.productType.join(' ')})`);
        }
        if(options.stats){
            request.addQueryParameter(SolrRequestDTO.PARAMETER_STATS, true);
        }
        if(options.statsField){
            request.addQueryParameter(SolrRequestDTO.PARAMETER_STATS_FIELD,`${this.escapeHash(this.escapeLucene(options.statsField))}`);
        }
        if(options.facetRange){
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FACET_RANGE,`${this.escapeHash(this.escapeLucene(options.facetRange))}`);
        }
        if(options.facetRangeArray) {
            options.facetRangeArray.forEach(val => {
                request.addQueryParameter(`${SolrRequestDTO.PARAMETER_FACET_RANGE}[]`,`${val}`);
            })
        }
        if(options.facetRangeStart){
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FACET_RANGE_START,`${options.facetRangeStart}`);
        }
        if(options.facetRangeEnd){
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FACET_RANGE_END,`${options.facetRangeEnd}`);
        }
        if(options.facetRangeGap){
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FACET_RANGE_GAP,`${this.escapeHash(this.escapeLucene(options.facetRangeGap))}`);
        }
        if (options.filters) {
            let skipQueryParamKeys = GenericConfig.skipQueryParamKeys || [];
            _.forEach(options.filters, (val, key) => {
                if (skipQueryParamKeys.includes(key)) {
                    request.addQueryParameter(key, val);
                } else {
                    if (_.isArray(val)) {
                        let v;
                        if (key.endsWith('_tdt') || key.endsWith('_dts')) {
                            v = _.map(val, (f) => {
                                return `${f}`;
                            });
                        } else {
                            v = _.map(val, (f) => {
                                return `"${this.escapeLucene(f)}"`;
                            });
                        }
                        request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `{!tag=${key}}${key}:(${v.join(' OR ')})`);
                    } else {
                        let v;
                        if (key.endsWith('_tdt') || key.endsWith('_dts')) {
                            v = `{!tag=${key}}${key}:${val}`;
                        } else {
                            v = `{!tag=${key}}${key}:"${this.escapeLucene(val)}"`
                        }
                        request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, v);
                    }
                }
            });
        }
        return this.http.get(`${this.getSolrUrl}?${request.paramsToString()}${options.extraQryString}`);
    }


    getEntity(entityId): Observable<any> {
        return new Observable((observer: Observer<any>) => {
            this.langSvc.getCurrentLanguage$().subscribe((currLang: ILanguage) => {
                this.getSolrEntityUrl(
                    entityId
                ).subscribe(responses => observer.next(responses));
            });
        });
    }

    getSolrEntityUrl(entityId) {
        const request = new SolrRequestDTO();
        request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `entity_id_i:${entityId}`);
        return this.http.get(`${this.getSolrUrl}?${request.paramsToString()}`);
    }


    getSolrEntityByVar(entityVar): Observable<any>  {
        const request = new SolrRequestDTO();
        request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `entity_var_s:${entityVar}`);
        return this.http.get(`${this.getSolrUrl}?${request.paramsToString()}`);
    }

    getSolrEntityByVarList(entityVarList) {
        const request = new SolrRequestDTO();
        request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `entity_var_s:(${entityVarList.join(' ')})`);
        return this.http.get(`${this.getSolrUrl}?${request.paramsToString()}`);
    }

    getSolrMediaByIdList(mediaIdList) {
        const currLang = this.langSvc.getCurrentLanguage();
        const request = new SolrRequestDTO();
        const count = mediaIdList.length;
        request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `media_id_i:(${mediaIdList.join(' ')})&fq[]=lang_code_s:${currLang.code}&rows=${count}`);
        return this.http.get(`${this.getSolrUrl}?${request.paramsToString()}`);
    }

    getEntityByHead(head): Observable<any> {
        return new Observable((observer: Observer<any>) => {
            this.langSvc.getCurrentLanguage$().subscribe((currLang: ILanguage) => {
                this.getSolrEntityUrlByHead(
                    head
                ).subscribe(responses => observer.next(responses));
            });
        });
    }

    getSolrEntityUrlByHead(head) {
        const request = new SolrRequestDTO();
        request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `head_s:${head}`);
        return this.http.get(`${this.getSolrUrl}?${request.paramsToString()}`);
    }

    handleAvailableFilters(availableFilters, request) {
        availableFilters.forEach(filter => {
            if ((filter !== 'Name_ss') && (filter != 'FamilyName_s') && (filter !== 'FamilyName_ss')) {
                request.addQueryParameter(SolrRequestDTO.PARAMETER_FACET_FIELD, `{!ex=${filter}}${filter}`);
            }
        });
    }

    escapeLucene(value) {
        if(!_.isString(value)) {
            return value;
        }
        value = value.replace(/&/g, '%26');
        value = value.replace(/\+/g, '%2B');
        // No idea why it needs lowercase b and not uppercase B over here
        var specials = ['b\\+', 'b\\-', '&', '!', '/', '(', ')', '{', '}', '[', ']', '^', /*'"',*/ '~', '*\\b', '?', ':', '\\', '"'];
        var regexp = new RegExp("(\\" + specials.join("|\\") + ")", "g");
        return value.replace(regexp, "\\$1");
    }

    escapeHash(string) {
        let escapeCharacters = "#";
        let charactersArray = string.split("");
        let parsedString = "";
        for(let character of charactersArray) {
            if(escapeCharacters.indexOf(character) === 0) {
                character = "%23";
            }
            parsedString += character
        }
        return parsedString;
    }
}
